import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Image,
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Spinner,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { getApi, deleteApi, deleteManyApi, postApi, putApi } from "common/services/api";
import { HasAccess } from "../../../../common/utils/accessUtils";
import CommonCheckTable from "common/components/checktable";
import { DeleteIcon, AddIcon, ChevronDownIcon, EditIcon, ViewIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import CommonDeleteModel from "common/components/commonDeleteModel";
import { toast } from "react-toastify";
import viewIcon from "common/assets/img/viewIcon.png";
import deleteIcon from "common/assets/img/deleteIcon.png";
import editIcon from "common/assets/img/editIcon.png";
import { constant } from "common/constant";
import { formatDateLong } from "common/utils/dateFormater";
import { globalStyles } from "common/theme/styles";
import CustomStatus from "common/components/status/CustomStatus";
import { API_PATHS } from "common/services/apiPaths";
import { accessModuleTitles } from "common/constant";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import ReactSelect from "react-select";
import { getLoggedInUser, getEffectiveRoleName, isSuperAdmin as checkIsSuperAdmin } from "common/utils/roleUtils";
import { ROLE } from "common/roles";
import { blockManualDateInput, blockDatePaste } from "common/utils/dateInputUtils";
import "common/assets/css/action-navbar.css";

const RequiredAsterisk = () => (
  <Text as="span" color="red.500" ms={1}>
    *
  </Text>
);

// Get today's date at midnight for validation
const getTodayAtMidnight = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .min(3, "Title must be at least 3 characters")
    .max(100, "Title must not exceed 100 characters")
    .matches(/^[a-zA-Z0-9\s\-_]+$/, "Title can only contain letters, numbers, spaces, hyphens and underscores"),
  description: Yup.string()
    .max(500, "Description must not exceed 500 characters"),
  status: Yup.string().required("Status is required").oneOf(["not-started", "in-progress", "on-hold", "completed", "pending"], "Invalid status"),
  priority: Yup.string().required("Priority is required").oneOf(["low", "medium", "high", "urgent"], "Invalid priority"),
  dueDate: Yup.date()
    .nullable()
    .min(getTodayAtMidnight(), "Due date cannot be in the past")
    .max(new Date('9999-12-31'), "Due date must be a valid year (4 digits)"),
});

const todoStatusColors = {
  "not-started": "#D97706",
  "in-progress": "#2563EB",
  "on-hold": "#64748B",
  "completed": "#059669",
  "pending": "#D97706",
};

const todoPriorityColors = {
  low: "#16A34A",
  medium: "#EAB308",
  high: "#DC2626",
  urgent: "#B91C1C",
};

const hasRequiredTodoFields = (values) => {
  return Boolean(
    values?.title?.trim() &&
    values?.status?.trim() &&
    values?.priority?.trim()
  );
};

const TodoList = () => {
  const title = "Todo List";
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedValues, setSelectedValues] = useState([]);
  const [deleteModel, setDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [menuSelectedDeleteItem, setMenuSelectedDeleteItem] = useState([]);
  const [todoModalOpen, setTodoModalOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [displaySearchData, setDisplaySearchData] = useState(false);
  const colors = globalStyles.colors;
  const [permission] = HasAccess([accessModuleTitles.Todo]);
  const navigate = useNavigate();
  const [canAssign, setCanAssign] = useState(false);
  const {
    isOpen: isTagsModalOpen,
    onOpen: onTagsModalOpen,
    onClose: onTagsModalClose
  } = useDisclosure();
  const [modalTags, setModalTags] = useState([]);

  // Redirect reviewers to document verification
  useEffect(() => {
    const user = getLoggedInUser();
    const userRole = user?.activeRoleId?.roleName || user?.role;
    if (userRole === "reviewer") {
      navigate("/recruitment/document-verification", { replace: true });
    }
  }, [navigate]);

  const [currentPage, setCurrentPage] = useState(() => {
    const paginationState = JSON.parse(localStorage.getItem("paginationState"));
    return paginationState?.pageName === title && paginationState?.currentPage
      ? paginationState.currentPage
      : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const paginationState = JSON.parse(localStorage.getItem("paginationState"));
    return paginationState?.pageName === title && paginationState?.pageSize
      ? paginationState.pageSize
      : 10;
  });

  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("");
  const [searchbox, setSearchbox] = useState(null);
  const [searchboxOutside, setSearchboxOutside] = useState("");
  const [finalSearchedData, setFinalSearchedData] = useState([]);
  const [currentStatusFilter, setCurrentStatusFilter] = useState("");
  const [metadata, setMetadata] = useState({});
  const [isCreatedByMeActive, setIsCreatedByMeActive] = useState(false);
  const [selectedUserFilter, setSelectedUserFilter] = useState("");

  // Get logged in user
  const loggedInUser = getLoggedInUser();
  const roleName = getEffectiveRoleName(loggedInUser);
  const hiringManagerRole = ROLE.hiringManager.toLowerCase();
  const isEmployee = roleName === "employee" || roleName === "Employee";
  const isSuperAdmin = checkIsSuperAdmin();
  const normalizedRoleName = String(roleName || "").toLowerCase();
  const isHiringManagerRole = normalizedRoleName === hiringManagerRole;
  const isAdminRoleFromRoles = loggedInUser?.roles?.some((role) => {
    const rName = String(role?.roleName || role || "").toLowerCase();
    return rName === "admin" || rName === "superadmin" || rName === "super_admin" || rName === "manager";
  });
  const isAdminRoleFromActiveRole = ["admin", "superadmin", "super_admin", "manager"].includes(normalizedRoleName);
  const isAdminRole = isSuperAdmin || isAdminRoleFromRoles || isAdminRoleFromActiveRole;
  const showNameFilter = isAdminRole || isHiringManagerRole;

  // Checkbox logic: return TRUE to ENABLE, FALSE to DISABLE (inverted by CommonCheckTable)
  const canSelectRow = useCallback((rowData) => {
    if (!permission?.delete) {
      return false; // Disable if no delete permission
    }
    const isCreator = rowData?.createBy?._id === loggedInUser?._id;

    // Check if user has admin role in any of their roles
    const hasAdminRole = loggedInUser?.roles?.some(role => {
      const rName = (role?.roleName || role || '').toLowerCase();
      const isAdmin = rName === 'admin' || rName === 'superadmin' || rName === 'super_admin' || rName === 'manager';
      return isAdmin;
    }) || isSuperAdmin;

    // Return TRUE to enable checkbox if user is creator OR admin
    return hasAdminRole || isCreator;
  }, [loggedInUser?._id, loggedInUser?.roles, permission?.delete, isSuperAdmin]);

  const fetchTodos = async (
    page = currentPage,
    limit = pageSize,
    sortKey = sortColumn,
    order = sortOrder,
    searchTerm = "",
    fetchAllData = false,
    status = "",
    createBy = "",
    userId = selectedUserFilter
  ) => {
    setIsLoading(true);
    try {
      let endpoint = `${API_PATHS.TODO_LIST}?page=${page}&limit=${limit}`;

      if (fetchAllData) {
        endpoint = `${API_PATHS.TODO_LIST}?allData=true`;
      }

      if (sortKey) {
        const sortParam = order === "desc" ? `-${sortKey}` : sortKey;
        endpoint += `&sort=${sortParam}`;
      }

      if (searchTerm && searchTerm.trim()) {
        endpoint += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      if (status) {
        endpoint += `&status=${encodeURIComponent(status.toLowerCase())}`;
      }

      if (createBy) {
        endpoint += `&createBy=${createBy}`;
      }

      if (userId) {
        endpoint += `&userId=${encodeURIComponent(userId)}`;
      }

      const response = await getApi(endpoint);
      if (response.status === 200) {
        if (fetchAllData) {
          setFinalSearchedData(response.data.data || []);
        } else {
          setData(response.data.data || []);
          setMetadata(response.data.metadata || {});
        }
      }
    } catch (error) {
      console.error("Error fetching todos:", error);
      toast.error(error.response?.data?.message || constant.somethingWrong);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch only on mount
  useEffect(() => {
    const createBy = isCreatedByMeActive ? loggedInUser?._id : "";
    const searchTerm = searchboxOutside || searchbox || "";
    fetchTodos(currentPage, pageSize, sortColumn, sortOrder, searchTerm, false, currentStatusFilter, createBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Get fresh currentUser for each call
        const currentUser = getLoggedInUser();
        const currentUserId = currentUser?._id?._id || currentUser?._id || currentUser?.id;

        const response = await getApi(`${API_PATHS.USER}?allData=true`);
        if (response.status === 200) {
          // Check the ACTIVE role (activeRoleId) - this respects role switching
          let activeRoleName = null;

          // Check activeRoleId first (populated role object from role switching)
          if (currentUser?.activeRoleId?.roleName) {
            activeRoleName = currentUser.activeRoleId.roleName;
          }
          // If activeRoleId is just an ID, find the role in roles array
          else if (currentUser?.activeRoleId && currentUser?.roles) {
            const activeRoleId = currentUser.activeRoleId._id || currentUser.activeRoleId;
            const activeRole = currentUser.roles.find(role => {
              const roleId = role._id || role;
              return roleId.toString() === activeRoleId.toString();
            });
            activeRoleName = activeRole?.roleName;
          }
          // Fallback: check if any role is employee (for backward compatibility)
          else if (currentUser?.roles?.some(role => {
            const roleName = role?.roleName || role;
            return roleName === 'employee' ||
              (typeof roleName === 'string' && roleName.toLowerCase() === 'employee');
          })) {
            activeRoleName = 'employee';
          }
          // Final fallback: check main role field
          else if (currentUser?.role) {
            activeRoleName = currentUser.role;
          }

          // Check if user is a hiring manager
          const isHiringManager = activeRoleName && activeRoleName.toLowerCase() === hiringManagerRole;

          // Check if current user is an admin (core roles only for assignment purposes)
          const isActuallyAdmin = isSuperAdmin || currentUser?.roles?.some((role) => {
            const rName = String(role?.roleName || role || "").toLowerCase();
            return rName === "admin" || rName === "superadmin" || rName === "super_admin";
          }) || ["admin", "superadmin", "super_admin"].includes(activeRoleName?.toLowerCase());

          // Check if user is a recruiter
          const isRecruiter = activeRoleName && activeRoleName.toLowerCase() === 'recruiter';

          let usersToShow = response.data.data || [];
          const currentUserId = currentUser?._id || currentUser?.id;

          const reports = usersToShow.filter(user => {
            const mId = user.managerId?._id || user.managerId || user.manager;
            return mId && currentUserId && String(mId) === String(currentUserId);
          });
          const isReportingManager = reports.length > 0;

          // Update assignment permission state
          setCanAssign(isActuallyAdmin || isHiringManager);

          // Apply role-based visibility filters
          const lowerActiveRole = String(activeRoleName || '').toLowerCase();

          if (lowerActiveRole === 'employee') {
            if (isReportingManager) {
              // Reporting Managers ONLY see their direct reports
              usersToShow = reports;
            } else {
              // Regular employees only see themselves (though field is hidden, it's safe to keep)
              usersToShow = usersToShow.filter(user => String(user._id) === String(currentUser._id));
            }
          } else if (isActuallyAdmin) {
            // Core Admins see everyone
          } else if (isHiringManager) {
            // Hiring Managers see everyone except Core Admins
            usersToShow = usersToShow.filter(user => {
              const userRoles = user.roles && Array.isArray(user.roles)
                ? user.roles.map(role => (role.roleName || role || '').toLowerCase())
                : [];
              const mainRole = (user.role || '').toLowerCase();
              const isAdminOption = userRoles.some(role =>
                role === 'admin' || role === 'superadmin' || role === 'super_admin'
              ) || ['admin', 'superadmin', 'super_admin'].includes(mainRole);
              return !isAdminOption;
            });
          } else if (isRecruiter) {
            // Recruiters see everyone except Core Admins and Hiring Managers
            usersToShow = usersToShow.filter(user => {
              const userRoles = user.roles && Array.isArray(user.roles)
                ? user.roles.map(role => (role.roleName || role || '').toLowerCase())
                : [];
              const mainRole = (user.role || '').toLowerCase();
              const isHiringManagerOption = userRoles.some(role => role === hiringManagerRole);
              const isAdminOption = userRoles.some(role =>
                role === 'admin' || role === 'superadmin' || role === 'super_admin'
              ) || ['admin', 'superadmin', 'super_admin'].includes(mainRole);
              return !isHiringManagerOption && !isAdminOption;
            });
          } else if (isReportingManager) {
            // Reporting Managers ONLY see their direct reports
            usersToShow = reports;
          } else {
            // Other roles: only see themselves
            usersToShow = usersToShow.filter(user => String(user._id) === String(currentUser._id));
          }

          const userOptions = usersToShow.map((user) => {
            // Get the actual user name (first + last name)
            const userName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.username || "Unknown User";

            return {
              value: user._id,
              label: userName,
            };
          });
          setUsers(userOptions);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    // Only fetch if we have a logged-in user ID
    if (loggedInUser?._id) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser?._id]);

  const handleOpenCreateModal = () => {
    setEditingTodo(null);
    setViewMode(false);
    setTodoModalOpen(true);
  };

  const handleOpenEditModal = async (todoId) => {
    setFormLoading(true);
    try {
      const response = await getApi(`${API_PATHS.TODO_LIST}${todoId}`);
      if (response.status === 200) {
        setEditingTodo(response.data.data);
        setViewMode(false);
        setTodoModalOpen(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || constant.somethingWrong);
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenViewModal = async (todoId) => {
    setFormLoading(true);
    try {
      const response = await getApi(`${API_PATHS.TODO_LIST}${todoId}`);
      if (response.status === 200) {
        setEditingTodo(response.data.data);
        setViewMode(true);
        setTodoModalOpen(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || constant.somethingWrong);
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseModal = () => {
    setTodoModalOpen(false);
    setEditingTodo(null);
    setViewMode(false);
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        title: values.title,
        description: values.description || "",
        status: values.status,
        priority: values.priority,
        dueDate: values.dueDate || null,
        assignedTo: values.assignedTo?.value || null,
        tags: Array.isArray(values.tags) ? values.tags : [],
      };

      if (editingTodo) {
        const response = await putApi(`${API_PATHS.TODO_UPDATE}${editingTodo._id}`, payload);
        if (response.status === 200) {
          toast.success("Todo updated successfully");
          handleCloseModal();
          fetchTodos();
        }
      } else {
        const response = await postApi(API_PATHS.POST_TODO, payload);
        if (response.status === 201 || response.status === 200) {
          toast.success("Todo created successfully");
          handleCloseModal();
          fetchTodos();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || constant.somethingWrong);
    } finally {
      setSubmitting(false);
    }
  };

  const statusOptionsForm = [
    { value: "not-started", label: "Not Started" },
    { value: "in-progress", label: "In Progress" },
    { value: "pending", label: "Pending" },
    { value: "completed", label: "Completed" },
    { value: "on-hold", label: "On Hold" },
  ];

  const priorityOptionsForm = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Critical" },
  ];

  const initialFormValues = editingTodo
    ? {
      title: editingTodo.title || "",
      description: editingTodo.description || "",
      status: editingTodo.status || "not-started",
      priority: editingTodo.priority || "medium",
      dueDate: editingTodo.dueDate ? new Date(editingTodo.dueDate).toISOString().split("T")[0] : "",
      assignedTo: editingTodo.assignedTo
        ? {
          value: editingTodo.assignedTo._id,
          label: (() => {
            // Check if assigned to current user
            const isCurrentUser = loggedInUser?._id && editingTodo.assignedTo._id === loggedInUser._id;
            const userName = `${editingTodo.assignedTo.firstName || ""} ${editingTodo.assignedTo.lastName || ""}`.trim() || editingTodo.assignedTo.username || "Unknown User";
            const roleNames = editingTodo.assignedTo.roles && Array.isArray(editingTodo.assignedTo.roles)
              ? editingTodo.assignedTo.roles.map(role => role.roleName || role).filter(Boolean).join(", ")
              : editingTodo.assignedTo.role || "User";
            return isCurrentUser ? `Me (${roleNames})` : `${userName} (${roleNames})`;
          })(),
        }
        : null,
      tags: editingTodo.tags || [],
    }
    : {
      title: "",
      description: "",
      status: "",
      priority: "",
      dueDate: "",
      assignedTo: null,
      tags: [],
    };

  const handleDeleteData = async (ids, selectedValues) => {
    try {
      const valuesToDelete = ids || selectedValues || [];
      let result;
      if (valuesToDelete.length === 1) {
        result = await deleteApi(API_PATHS.TODO_DELETE, valuesToDelete[0]);
        // Check if result is an error (deleteApi returns error object instead of throwing)
        if (result && result.response) {
          const errorMessage = result.response?.data?.message || result.message || constant.somethingWrong;
          toast.error(errorMessage);
          return;
        }
        toast.success("Todo deleted successfully");
      } else {
        result = await deleteManyApi(API_PATHS.TODO_DELETE_MANY, { ids: valuesToDelete });
        // Check if result is an error
        if (result && result.response) {
          const errorMessage = result.response?.data?.message || result.message || constant.somethingWrong;
          toast.error(errorMessage);
          return;
        }
        toast.success(`${valuesToDelete.length} todos deleted successfully`);
      }
      setDelete(false);
      setSelectedValues([]);
      setMenuSelectedDeleteItem([]);
      const createBy = isCreatedByMeActive ? loggedInUser?._id : "";
      const searchTerm = searchboxOutside || searchbox || "";
      fetchTodos(currentPage, pageSize, sortColumn, sortOrder, searchTerm, false, currentStatusFilter, createBy, selectedUserFilter);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.message || error.message || constant.somethingWrong);
    }
  };

  const handleSearchChange = (term) => {
    const pageToFetch = term?.trim() === "" ? currentPage : 1;

    if (term?.trim() !== "" && currentPage !== 1) {
      setCurrentPage(1);
    }

    // Get createBy value if filter is active
    const createBy = isCreatedByMeActive ? loggedInUser?._id : "";

    // Include current status filter
    fetchTodos(
      pageToFetch,
      pageSize,
      sortColumn,
      sortOrder,
      term || "",
      false,
      currentStatusFilter,
      createBy,
      selectedUserFilter
    );
  };

  const handleSortChange = (accessorKey) => {
    let newSortOrder = "asc";

    if (sortColumn === accessorKey) {
      newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    }

    setSortColumn(accessorKey);
    setSortOrder(newSortOrder);

    // Get createBy value if filter is active
    const createBy = isCreatedByMeActive ? loggedInUser?._id : "";

    // Include current search and status filter
    const searchTerm = searchboxOutside || searchbox || "";
    fetchTodos(
      currentPage,
      pageSize,
      accessorKey,
      newSortOrder,
      searchTerm,
      false,
      currentStatusFilter,
      createBy,
      selectedUserFilter
    );
  };

  const handleCreatedByMe = useCallback(async () => {
    const userId = loggedInUser?._id;
    if (!userId) {
      toast.error("User not found");
      return;
    }

    // Toggle the filter
    const newState = !isCreatedByMeActive;
    setIsCreatedByMeActive(newState);

    // If activating filter, fetch with createBy; if deactivating, fetch without
    const createBy = newState ? userId : "";

    // Fetch with current search and status filter maintained
    const searchTerm = searchboxOutside || searchbox || "";
    await fetchTodos(
      currentPage,
      pageSize,
      sortColumn,
      sortOrder,
      searchTerm,
      false,
      currentStatusFilter,
      createBy,
      selectedUserFilter
    );
  }, [
    isCreatedByMeActive,
    currentPage,
    pageSize,
    sortColumn,
    sortOrder,
    searchbox,
    searchboxOutside,
    currentStatusFilter,
    loggedInUser?._id,
    selectedUserFilter,
  ]);

  const statusOptions = [
    { value: "not-started", label: "Not Started", color: "yellow" },
    { value: "in-progress", label: "In Progress", color: "blue" },
    { value: "pending", label: "Pending", color: "yellow" },
    { value: "completed", label: "Completed", color: "green" },
    { value: "on-hold", label: "On Hold", color: "gray" },
  ];

  const handleStatusFilterChange = useCallback(
    (selectedStatusLabel) => {
      // Handle "All Status" selection (empty string)
      if (!selectedStatusLabel || selectedStatusLabel === "") {
        setCurrentStatusFilter("");
        setCurrentPage(1);
        const createBy = isCreatedByMeActive ? loggedInUser?._id : "";
        const searchTerm = searchboxOutside || searchbox || "";
        fetchTodos(1, pageSize, sortColumn, sortOrder, searchTerm, false, "", createBy, selectedUserFilter);
        return;
      }

      // Map label to value since CommonCheckTable passes the label
      const statusOption = statusOptions.find(opt => opt.label === selectedStatusLabel);
      const selectedStatusValue = statusOption ? statusOption.value : selectedStatusLabel;

      // Update current status filter state
      setCurrentStatusFilter(selectedStatusValue);
      setCurrentPage(1);

      // Get createBy value if filter is active
      const createBy = isCreatedByMeActive ? loggedInUser?._id : "";

      // Include current search term
      const searchTerm = searchboxOutside || searchbox || "";
      fetchTodos(
        1,
        pageSize,
        sortColumn,
        sortOrder,
        searchTerm,
        false,
        selectedStatusValue,
        createBy,
        selectedUserFilter
      );
    },
    [
      isCreatedByMeActive,
      loggedInUser?._id,
      pageSize,
      sortColumn,
      sortOrder,
      searchbox,
      statusOptions,
      selectedUserFilter,
    ]
  );

  const handleUserFilterChange = (selectedOption) => {
    const userId = selectedOption?.value || "";
    setSelectedUserFilter(userId);
    setCurrentPage(1);
    const createBy = isCreatedByMeActive ? loggedInUser?._id : "";
    const searchTerm = searchboxOutside || searchbox || "";
    fetchTodos(1, pageSize, sortColumn, sortOrder, searchTerm, false, currentStatusFilter, createBy, userId);
  };

  const priorityOptions = [
    { value: "low", label: "Low", color: "gray" },
    { value: "medium", label: "Medium", color: "yellow" },
    { value: "high", label: "High", color: "orange" },
    { value: "urgent", label: "Critical", color: "red" },
  ];

  const actionHeader = {
    Header: "Action",
    isSortable: false,
    center: true,
    cell: ({ row }) => {
      // Get the creator ID from the row
      const creatorId = row?.original?.createBy?._id || row?.original?.createBy;
      const isCreator = loggedInUser?._id && creatorId === loggedInUser._id;

      // Get the assigned user ID
      const assignedId = row?.original?.assignedTo?._id || row?.original?.assignedTo;

      // Check if this is a self-assigned todo (creator assigned only to themselves)
      // This includes employee personal todos
      const isSelfAssignedTodo = creatorId && assignedId && creatorId === assignedId;

      // For admin/non-employee viewing someone else's self-assigned todo: only view, no edit/delete
      // This protects employee personal todos from admin editing
      const isViewingOthersSelfTodo = !isEmployee && isSelfAssignedTodo && !isCreator;

      // Check if current user is a hiring manager
      const currentUserRoles = loggedInUser?.roles || [];
      const isHiringManager = currentUserRoles.some(role => {
        const roleName = (role?.roleName || role || '').toLowerCase();
        return roleName === hiringManagerRole;
      }) || (loggedInUser?.activeRoleId?.roleName || '').toLowerCase() === hiringManagerRole;

      // Check if current user is a recruiter
      const isRecruiterUser = currentUserRoles.some(role => {
        const roleName = (role?.roleName || role || '').toLowerCase();
        return roleName === 'recruiter';
      }) || (loggedInUser?.activeRoleId?.roleName || '').toLowerCase() === 'recruiter';

      // Check if current user is an admin
      const isAdminUser = currentUserRoles.some(role => {
        const roleName = (role?.roleName || role || '').toLowerCase();
        return roleName === 'admin' || roleName === 'superadmin' || roleName === 'super_admin' || roleName === 'manager';
      }) || ['admin', 'superadmin', 'super_admin'].includes((loggedInUser?.role || '').toLowerCase());

      // Check if todo was created by an admin
      const creatorRoles = row?.original?.createBy?.roles || [];
      const creatorMainRole = (row?.original?.createBy?.role || '').toLowerCase();
      const isCreatedByAdmin = creatorRoles.some(role => {
        const roleName = (role?.roleName || role || '').toLowerCase();
        return roleName === 'admin' || roleName === 'superadmin' || roleName === 'super_admin' || roleName === 'manager';
      }) || creatorMainRole === 'admin' || creatorMainRole === 'superadmin' || creatorMainRole === 'super_admin';

      // Check if todo was created by hiring manager or recruiter
      const isCreatedByHMOrRecruiter = creatorRoles.some(role => {
        const roleName = (role?.roleName || role || '').toLowerCase();
        return roleName === hiringManagerRole || roleName === 'recruiter';
      });

      // Check if todo was created by hiring manager specifically
      const isCreatedByHiringManager = creatorRoles.some(role => {
        const roleName = (role?.roleName || role || '').toLowerCase();
        return roleName === hiringManagerRole;
      });

      // Hiring manager or recruiter viewing admin-created todo: only view, no edit/delete
      const isHMOrRecruiterViewingAdminTodo = (isHiringManager || isRecruiterUser) && isCreatedByAdmin && !isCreator;

      // Admin viewing hiring manager or recruiter created todo: only view, no edit/delete
      const isAdminViewingHMOrRecruiterTodo = isAdminUser && isCreatedByHMOrRecruiter && !isCreator;

      // Recruiter viewing hiring manager-created todo: only view, no edit/delete
      const isRecruiterViewingHMTodo = isRecruiterUser && isCreatedByHiringManager && !isCreator;

      // For employees: only show actions if they created the todo
      // For admin viewing other's self-todo: only view
      // For admin viewing HM/recruiter todo: only view
      // For hiring manager/recruiter viewing admin's todo: only view
      // For recruiter viewing HM's todo: only view
      // For other non-employees: use regular permission checks
      const shouldHideActions = isViewingOthersSelfTodo || isHMOrRecruiterViewingAdminTodo || isAdminViewingHMOrRecruiterTodo || isRecruiterViewingHMTodo;
      const canEdit = isEmployee ? isCreator : (shouldHideActions ? false : permission?.update);
      const canView = true; // Everyone can view
      const canDelete = isEmployee ? isCreator : (shouldHideActions ? false : permission?.delete);

      const noActionAvailable = !canEdit && !canView && !canDelete;

      return (
        <Flex justify="center" align="center" w="100%">
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              size="sm"
              fontWeight="bold"
              fontSize="xl"
              color="gray.600"
              _hover={{ bg: "gray.100" }}
              px={2}
              minW="auto"
              onClick={(e) => e.stopPropagation()}
            >
              ⋮
            </MenuButton>
            <MenuList minW="120px" shadow="md">
              {canView && (
                <MenuItem
                  icon={<ViewIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenViewModal(row?.values?._id);
                  }}
                >
                  View
                </MenuItem>
              )}
              {canEdit && (
                <MenuItem
                  icon={<EditIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditModal(row?.values?._id);
                  }}
                >
                  Edit
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem
                  icon={<DeleteIcon color="red.500" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDelete(true);
                    setSelectedValues([row?.values?._id]);
                    setMenuSelectedDeleteItem([row?.values?._id]);
                  }}
                  color="red.500"
                >
                  Delete
                </MenuItem>
              )}
              {noActionAvailable && (
                <MenuItem isDisabled>No actions available</MenuItem>
              )}
            </MenuList>
          </Menu>
        </Flex>
      );
    },
  };

  const baseColumns = [
    {
      Header: "#",
      accessor: "_id",
      isSortable: false,
      width: 5,
    },
    {
      Header: "Title",
      accessor: "title",
      type: "text",
      cell: (cell) => (
        <Text
          as="span"
          cursor={cell?.value ? "pointer" : "default"}
          fontWeight="400"
          color={cell?.value ? colors.cta["100"] : "inherit"}
          _hover={cell?.value ? { textDecoration: "underline", color: colors.cta["100"] } : undefined}
          onClick={() => {
            handleOpenViewModal(cell?.row?.original?._id);
          }}
          whiteSpace="nowrap"
        >
          {cell?.value || "-"}
        </Text>
      ),
    },
    {
      Header: "Due Date",
      accessor: "dueDate",
      type: "date",
      cell: (cell) => (
        <Text>{cell?.value ? formatDateLong(cell.value) : "-"}</Text>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      type: "text",
      width: 140,
      cell: (cell) => {
        const status = statusOptions.find((s) => s.value === cell?.value);
        const statusLabel = status?.label || cell?.value || "-";
        return (
          <CustomStatus
            statusText={statusLabel}
            color={todoStatusColors[cell?.value] || colors.text?.["200"]}
          />
        );
      },
    },
    {
      Header: "Priority",
      accessor: "priority",
      type: "text",
      cell: (cell) => {
        const priority = priorityOptions.find((p) => p.value === cell?.value);
        const priorityLabel = priority?.label || cell?.value || "-";
        return (
          <CustomStatus
            statusText={priorityLabel}
            color={todoPriorityColors[cell?.value] || colors.text?.["200"]}
          />
        );
      },
    },

    {
      Header: "Assigned To",
      accessor: "assignedTo",
      type: "text",
      cell: (cell) => {
        const assigned = cell?.value;

        // Check if assigned user exists
        if (!assigned) {
          return <Text>Unassigned</Text>;
        }

        // Get user name
        const firstName = assigned?.firstName || "";
        const lastName = assigned?.lastName || "";
        const userName = `${firstName} ${lastName}`.trim() || assigned?.username || assigned?.email || "";

        return (
          <Text>
            {userName || "Unassigned"}
          </Text>
        );
      },
    },
    {
      Header: "Created By",
      accessor: "createBy",
      type: "text",
      cell: (cell) => {
        const creator = cell?.value;

        if (!creator) {
          return <Text>-</Text>;
        }

        const firstName = creator?.firstName || "";
        const lastName = creator?.lastName || "";
        const userName = `${firstName} ${lastName}`.trim() || creator?.username || creator?.email || "";

        return (
          <Text>
            {userName || "-"}
          </Text>
        );
      },
    },
    {
      Header: "Tags",
      accessor: "tags",
      type: "text",
      cell: (cell) => {
        const tags = cell?.value || [];
        const displayTags = tags.slice(0, 2);
        const remainingCount = tags.length - 2;

        const formatTag = (tag) => tag
          .split(/\s+/)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');

        return (
          <Wrap spacing={1} align="center">
            {displayTags.map((tag, index) => (
              <WrapItem key={index}>
                <Tag size="sm" borderRadius="full" variant="solid" bg="#3C2FC0" color="white">
                  <TagLabel>{formatTag(tag)}</TagLabel>
                </Tag>
              </WrapItem>
            ))}
            {remainingCount > 0 && (
              <WrapItem>
                {isEmployee ? (
                  <Tag
                    size="sm"
                    borderRadius="full"
                    variant="solid"
                    bg="gray.100"
                    color="#3C2FC0"
                  >
                    <TagLabel fontWeight="bold">+{remainingCount}</TagLabel>
                  </Tag>
                ) : (
                  <Tag
                    size="sm"
                    borderRadius="full"
                    variant="solid"
                    bg="gray.100"
                    color="#3C2FC0"
                    cursor="pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalTags(tags);
                      onTagsModalOpen();
                    }}
                    _hover={{ bg: "gray.200" }}
                  >
                    <TagLabel fontWeight="bold">+{remainingCount}</TagLabel>
                  </Tag>
                )}
              </WrapItem>
            )}
            {tags.length === 0 && <Text>-</Text>}
          </Wrap>
        );
      },
    },
    {
      Header: "Created At",
      accessor: "createdAt",
      type: "date",
      cell: (cell) => <Text>{cell?.value ? formatDateLong(cell.value) : "-"}</Text>,
    },
  ];

  const tableColumns = isEmployee
    ? [...baseColumns.filter((col) => ["#", "Title", "Status", "Priority", "Due Date", "Tags"].includes(col.Header)), actionHeader]
    : (isAdminRole || isHiringManagerRole)
      ? [...baseColumns, actionHeader]
      : [...baseColumns.filter((col) => col.Header !== "Assigned To"), actionHeader];

  const [columns, setColumns] = useState([...tableColumns]);
  const [selectedColumns, setSelectedColumns] = useState([...tableColumns]);
  const dataColumn = tableColumns?.filter((item) =>
    selectedColumns?.find((colum) => colum?.Header === item.Header)
  );

  return (
    <Box className={isEmployee ? "" : "pt-[30px] overviewtable"}>
      <CommonCheckTable
        title={title}
        isLoading={isLoading}
        columnData={columns}
        dataColumn={dataColumn}
        allData={data}
        tableData={data}
        setSearchDisplay={setDisplaySearchData}
        tableCustomFields={[]}
        access={isEmployee ? { ...permission, create: true, update: true } : permission}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isDynamic={true}
        metadata={metadata}
        searchbox={searchbox}
        setSearchbox={setSearchbox}
        searchboxOutside={searchboxOutside}
        setSearchboxOutside={setSearchboxOutside}
        handleSearchChange={handleSearchChange}
        handleSortChange={handleSortChange}
        isOpen={isOpen}
        onOpen={handleOpenCreateModal}
        onClose={onClose}
        setDelete={setDelete}
        showNavbarActionForEmployee={true}
        showInlineTitle={true}
        statusFilter={true}
        filterStatusOptions={statusOptions.map(opt => ({ value: opt.value, label: opt.label }))}
        handleStatusFilterChange={handleStatusFilterChange}
        currentStatusFilter={currentStatusFilter}
        showCreatedByMeFilter={!isEmployee}
        handleCreatedByMe={handleCreatedByMe}
        isCreatedByMeActive={isCreatedByMeActive}
        userFilter={selectedUserFilter}
        statusFilterAlign="right"
        customRightFilters={
          showNameFilter ? (
            <Box flexShrink={0}>
              <ReactSelect
                className="basic-single-select tableReactSelect reactSelect"
                classNamePrefix="select"
                placeholder="Filter by Name"
                isSearchable
                isClearable
                value={users.find((user) => user.value === selectedUserFilter) || null}
                onChange={handleUserFilterChange}
                options={users}
              />
            </Box>
          ) : null
        }
        createByFilter={isCreatedByMeActive ? loggedInUser?._id : ""}
        fetchData={fetchTodos}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        finalSearchedData={finalSearchedData}
        setFinalSearchedData={setFinalSearchedData}
        checkBox={canSelectRow}
        disableDefaultCellNavigation={isEmployee}
        onRowClick={
          isEmployee
            ? undefined
            : (row) => {
              handleOpenViewModal(row?.original?._id);
            }
        }
      />

      <CommonDeleteModel
        isOpen={deleteModel}
        onClose={() => {
          setDelete(false);
          setSelectedValues([]);
          setMenuSelectedDeleteItem([]);
        }}
        handleDeleteData={handleDeleteData}
        ids={selectedValues}
        selectedValues={selectedValues}
        type="todo"
        onCancel={() => {
          setSelectedValues([]);
          setMenuSelectedDeleteItem([]);
        }}
      />

      {/* Todo Create/Edit Modal */}
      <Modal isOpen={todoModalOpen} onClose={handleCloseModal} size={{ base: "full", md: "4xl" }} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingTodo ? (viewMode ? "View Todo" : "Edit Todo") : "Create Todo"}
          </ModalHeader>
          <ModalCloseButton />
          {formLoading ? (
            <ModalBody>
              <Flex justify="center" align="center" py={8}>
                <Spinner size="xl" />
              </Flex>
            </ModalBody>
          ) : (
            <Formik
              initialValues={initialFormValues}
              validationSchema={validationSchema}
              onSubmit={handleFormSubmit}
              enableReinitialize
              validateOnMount
            >
              {({ values, errors, touched, setFieldValue, isSubmitting, isValid, dirty }) => {
                const canSubmitTodo =
                  hasRequiredTodoFields(values) &&
                  isValid &&
                  (editingTodo ? dirty : true) &&
                  (isEmployee || permission?.create || permission?.update);

                return (
                <Form style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                  <ModalBody>
                    <FormControl mb={4} isInvalid={errors.title && touched.title}>
                      <FormLabel>Title <Text as="span" color="red.500">*</Text></FormLabel>
                      <Field
                        as={Input}
                        name="title"
                        placeholder="Enter todo title"
                        disabled={viewMode}
                        maxLength={100}
                      />
                      {errors.title && touched.title && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.title}
                        </Text>
                      )}
                      <Text color="gray.500" fontSize="xs" mt={1} textAlign="right">
                        {values.title?.length || 0}/100 characters
                      </Text>
                    </FormControl>

                    <Flex gap={4} mb={4}>
                      <FormControl isInvalid={errors.status && touched.status} flex={1}>
                        <FormLabel>Status <Text as="span" color="red.500">*</Text></FormLabel>
                        <Field
                          as={Select}
                          name="status"
                          disabled={viewMode}
                        >
                          <option value="" hidden>
                            Select Status
                          </option>
                          {statusOptionsForm.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                        {errors.status && touched.status && (
                          <Text color="red.500" fontSize="sm" mt={1}>
                            {errors.status}
                          </Text>
                        )}
                      </FormControl>

                      <FormControl isInvalid={errors.priority && touched.priority} flex={1}>
                        <FormLabel>Priority <Text as="span" color="red.500">*</Text></FormLabel>
                        <Field
                          as={Select}
                          name="priority"
                          disabled={viewMode}
                        >
                          <option value="" hidden>
                            Select Priority
                          </option>
                          {priorityOptionsForm.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                        {errors.priority && touched.priority && (
                          <Text color="red.500" fontSize="sm" mt={1}>
                            {errors.priority}
                          </Text>
                        )}
                      </FormControl>

                      <FormControl isInvalid={errors.dueDate && touched.dueDate} flex={1}>
                        <FormLabel>Due Date</FormLabel>
                        <Field name="dueDate">
                          {({ field, form }) => (
                            <Input
                              {...field}
                              type="date"
                              disabled={viewMode}
                              min={new Date().toISOString().split('T')[0]}
                              max="9999-12-31"
                              maxLength={10}
                              onKeyDown={blockManualDateInput}
                              onPaste={blockDatePaste}
                              onInput={(e) => {
                                // Restrict input length to 10 characters (YYYY-MM-DD)
                                if (e.target.value.length > 10) {
                                  e.target.value = e.target.value.slice(0, 10);
                                }
                              }}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Validate year doesn't exceed 4 digits
                                if (value) {
                                  const yearPart = value.split('-')[0];
                                  if (yearPart && yearPart.length > 4) {
                                    return; // Don't update if year exceeds 4 digits
                                  }
                                }
                                form.setFieldValue('dueDate', value);
                              }}
                            />
                          )}
                        </Field>
                        {errors.dueDate && touched.dueDate && (
                          <Text color="red.500" fontSize="sm" mt={1}>
                            {errors.dueDate}
                          </Text>
                        )}
                      </FormControl>
                    </Flex>

                    {canAssign && (
                      <Flex mb={4}>
                        <FormControl flex={1}>
                          <FormLabel>Assign To</FormLabel>
                          <ReactSelect
                            options={users}
                            value={values.assignedTo}
                            onChange={(selected) => setFieldValue("assignedTo", selected)}
                            isDisabled={viewMode}
                            isClearable
                            placeholder="Select user..."
                            isSearchable={users.length > 1}
                          />
                          {users.length === 1 && isEmployee && (
                            <Text color="gray.500" fontSize="xs" mt={1}>
                              As an employee, you can only assign tasks to yourself or your reports.
                            </Text>
                          )}
                        </FormControl>
                      </Flex>
                    )}

                    <FormControl mb={4}>
                      <FormLabel>Tags</FormLabel>
                      {!viewMode && (
                        <Input
                          placeholder="Type a tag and press Enter"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const val = e.target.value.trim();
                              if (val) {
                                const formattedTag = val
                                  .split(/\s+/)
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                  .join(' ');

                                if (!values.tags.includes(formattedTag)) {
                                  setFieldValue("tags", [...values.tags, formattedTag]);
                                  e.target.value = "";
                                }
                              }
                            }
                          }}
                        />
                      )}
                      {values.tags?.length > 0 && (
                        <Wrap spacing={2} mt={2}>
                          {values.tags.map((tag, index) => (
                            <WrapItem key={index}>
                              <Tag
                                size="md"
                                borderRadius="full"
                                variant="solid"
                                bg="#3C2FC0"
                                color="white"
                              >
                                <TagLabel>{tag}</TagLabel>
                                {!viewMode && (
                                  <TagCloseButton
                                    onClick={() => {
                                      const newTags = values.tags.filter((_, i) => i !== index);
                                      setFieldValue("tags", newTags);
                                    }}
                                  />
                                )}
                              </Tag>
                            </WrapItem>
                          ))}
                        </Wrap>
                      )}
                    </FormControl>


                    <FormControl mb={4} isInvalid={errors.description && touched.description}>
                      <FormLabel>Description</FormLabel>
                      <Field
                        as={Textarea}
                        name="description"
                        placeholder="Enter todo description"
                        rows={4}
                        disabled={viewMode}
                        maxLength={500}
                      />
                      {errors.description && touched.description && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.description}
                        </Text>
                      )}
                      <Text color="gray.500" fontSize="xs" mt={1} textAlign="right">
                        {values.description?.length || 0}/500 characters
                      </Text>
                    </FormControl>
                  </ModalBody>

                  {!viewMode && (
                    <ModalFooter>
                      <Button variant="ghost" mr={3} onClick={handleCloseModal}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        bgColor="#3C2FC0"
                        color={'white'}
                        isLoading={isSubmitting}
                        disabled={!canSubmitTodo}
                      >
                        {editingTodo ? "Update" : "Create"}
                      </Button>
                    </ModalFooter>
                  )}
                  {viewMode && (
                    <ModalFooter>
                      <Button onClick={handleCloseModal}>Close</Button>
                    </ModalFooter>
                  )}
                </Form>
              )}}
            </Formik>
          )}
        </ModalContent>
      </Modal>

      {/* All Tags Modal */}
      <Modal isOpen={isTagsModalOpen} onClose={onTagsModalClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(2px)" />
        <ModalContent borderRadius="xl" p={2}>
          <ModalHeader pb={2}>All Tags</ModalHeader>
          <ModalCloseButton mt={2} />
          <ModalBody pb={6}>
            <Wrap spacing={2}>
              {modalTags.map((tag, index) => (
                <WrapItem key={index}>
                  <Tag
                    size="md"
                    borderRadius="full"
                    variant="solid"
                    bg="#3C2FC0"
                    color="white"
                    px={3}
                    py={1}
                  >
                    <TagLabel>
                      {tag
                        .split(/\s+/)
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ')}
                    </TagLabel>
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TodoList;
